import type { HarmonyPackage } from '@rhythmic/db/types'
import type { Collection, Ref, UseLiveQueryReturn } from '@tanstack/vue-db'
import { and, eq, useLiveQuery } from '@tanstack/vue-db'
import { toRef } from '@vueuse/core'
import type { MaybeRefOrGetter } from 'vue'
import { toValue, watch } from 'vue'
import { useRhythmicLogger } from '#imports'
import { getPlanCollection } from '../contexts'

export interface QueryReturnType {
  query: UseLiveQueryReturn<HarmonyPackage>
  collection: Collection<any, any, any>
}

export function useHarmonyPlansForWorkspace(
  workspaceId: MaybeRefOrGetter<string | null | undefined>,
  teamId?: MaybeRefOrGetter<string | null | undefined>,
): UseLiveQueryReturn<HarmonyPackage> | undefined {
  const logger = useRhythmicLogger('useHarmonyPlansForWorkspace')
  const resolvedWorkspaceId = toRef(workspaceId)
  const resolvedTeamId = toRef(teamId)

  if (!resolvedWorkspaceId.value) {
    logger.warn('No workspace ID provided to useHarmonyPlansForWorkspace')
    return undefined
  }

  const packages = getPlanCollection(toValue(resolvedWorkspaceId) as string)

  const plansArePartOfThisWorkspace = ({ packages }: { packages: Ref<HarmonyPackage> }) =>
    eq(packages.workspace_id, resolvedWorkspaceId.value)
  const plansArePartOfThisTeam = ({ packages }: { packages: Ref<HarmonyPackage> }) =>
    eq(packages.team_id, resolvedTeamId.value)

  const filteredPlans = ({ packages }: { packages: Ref<HarmonyPackage> }) => {
    if (resolvedTeamId.value) {
      return and(plansArePartOfThisWorkspace({ packages }), plansArePartOfThisTeam({ packages }))
    }
    return plansArePartOfThisWorkspace({ packages })
  }

  const query = useLiveQuery(
    (builder) => {
      return builder
        .from({ packages })
        .where(filteredPlans)
        .orderBy(({ packages }) => packages.created_at, 'desc')
    },
    [resolvedWorkspaceId, resolvedTeamId],
  )

  return query
}
